/**
 * §10.7 Company Milestones — interactive vertical timeline.
 * Past / Current / Future badges make the timeline honest.
 */

import React, { useState } from 'react'
import clsx from 'clsx'
import { GitCommit, ChevronDown, Sparkles } from 'lucide-react'
import { Container, Eyebrow, SectionHeading } from '@/components/landing/design-system'
import { MILESTONES } from '../data'
import { TONE } from '../_shared'

const STATUS = {
  past:    { l: 'Delivered',  c: 'text-emerald-400', b: 'border-emerald-400/30', bg: 'bg-emerald-400/[0.08]' },
  current: { l: 'In progress', c: 'text-brand-gold', b: 'border-brand-gold/35', bg: 'bg-brand-gold/[0.10]' },
  future:  { l: 'Future goal',  c: 'text-amber-400',   b: 'border-amber-400/30',    bg: 'bg-amber-400/[0.08]'  },
}

export default function Milestones() {
  const currentIdx = MILESTONES.findIndex((m) => m.status === 'current')
  const [openId, setOpenId] = useState(MILESTONES[currentIdx >= 0 ? currentIdx : 0]?.id)

  return (
    <section className="relative py-24 md:py-28">
      <Container size="wide" className="flex flex-col gap-10">
        <div className="flex flex-col items-center text-center gap-5 max-w-3xl mx-auto">
          <Eyebrow>Company Milestones</Eyebrow>
          <SectionHeading align="center"
            title={<>Every step so far,{' '}<span className="bg-gradient-to-r from-brand-gold to-[#FFE17A] bg-clip-text text-transparent">and every step ahead.</span></>}
            subtitle="Delivered milestones are the ones we shipped.  In-progress items are the current focus.  Future items are goals — clearly marked, never over-promised."
          />
        </div>

        <div className="relative">
          <div aria-hidden className="pointer-events-none absolute left-4 md:left-5 top-4 bottom-4 w-px bg-gradient-to-b from-emerald-500/40 via-brand-gold/50 to-amber-400/40" />

          <div className="flex flex-col gap-3">
            {MILESTONES.map((m, i) => {
              const tone = TONE[m.tone] || TONE.orange
              const st = STATUS[m.status] || STATUS.past
              const open = openId === m.id
              return (
                <div key={m.id} className="relative pl-12 md:pl-14">
                  {/* dot */}
                  <span className="absolute left-2 md:left-3 top-4 inline-flex h-5 w-5 items-center justify-center rounded-full border-2 border-black"
                    style={{ background: tone.hex, boxShadow: `0 0 0 3px rgba(${tone.rgb},0.25)` }}>
                    <GitCommit className="h-2.5 w-2.5 text-black" strokeWidth={3} />
                  </span>

                  <button onClick={() => setOpenId(open ? null : m.id)} aria-expanded={open}
                    className={clsx(
                      'w-full text-left rounded-2xl border transition-all overflow-hidden',
                      open
                        ? 'border-white/20 bg-white/[0.04]'
                        : 'border-white/10 bg-white/[0.02] hover:border-white/15 hover:bg-white/[0.03]',
                    )}>
                    <div className="relative p-4 md:p-5">
                      <div className="pointer-events-none absolute inset-0 opacity-25"
                        style={{ background: `radial-gradient(60% 40% at 0% 0%, rgba(${tone.rgb},0.10), transparent 70%)` }} />
                      <div className="relative flex items-center gap-3">
                        <span className="text-[10.5px] font-mono text-zinc-500">M {String(i + 1).padStart(2, '0')}</span>
                        <div className="flex-1 min-w-0">
                          <div className="text-[15px] font-semibold text-white leading-tight">{m.label}</div>
                          <div className="text-[11px] font-mono text-zinc-500 mt-0.5">{m.period}</div>
                        </div>
                        <span className={clsx('inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider', st.b, st.bg, st.c)}>
                          {st.l}
                        </span>
                        <ChevronDown className={clsx('h-4 w-4 text-zinc-500 shrink-0 transition-transform ml-2', open && 'rotate-180')} />
                      </div>
                      {open && (
                        <p className="relative text-[13px] text-zinc-300 leading-relaxed mt-3 pt-3 border-t border-white/5">{m.body}</p>
                      )}
                    </div>
                  </button>
                </div>
              )
            })}
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-xl px-5 md:px-6 py-4 flex items-start gap-3">
          <Sparkles className="h-4 w-4 text-brand-gold shrink-0 mt-0.5" />
          <p className="text-sm text-zinc-300 leading-relaxed">
            <span className="font-semibold text-white">We publish roadmaps only when we can commit.</span>{' '}
            Future milestones are directional goals, not contractual promises.  Delivered items ship to every tenant automatically with zero downtime.
          </p>
        </div>
      </Container>
    </section>
  )
}
